import { useSelector } from "react-redux";
import { PagesWrapper } from "../../styles/components/PagesWrapper.styled";
import WelcomeCard from "./WelcomeCard/WelcomeCard";
import { RenewalBanner, ReferralCard, TelegramCard } from "../StudentGrowth/StudentGrowth";
import { LuPlayCircle, LuScrollText, LuFileText, LuAward, LuSparkles } from "react-icons/lu";
import TopPageTitle from "../TopPageTitle/TopPageTitle";
import * as styled from "./GeneralPage.styled";
import ProgressBlock from "./ProgressBlock/ProgressBlock";
import FeatureBlock from "./FeatureBlock/FeatureBlock";
import { useState, useMemo, useEffect } from "react";
import GeneralDropDown from "../GeneralDropDown/GeneralDropDown";
import AdviceDropDown from "../AdviceDropDown/AdviceDropDown";
import axios from "../../axios";
import {
  adviceStudent,
  newFeatureStudent,
  newFeatureTitleStudent,
  rules,
} from "../../helpers/generalStudentTexts/generalStudentTexts";
import { studentLevels } from "../../helpers/lessonsLevels/studentLevels";
import { teacherLevels } from "../../helpers/lessonsLevels/teacherLevels";
import {
  adviceTeacher,
  newFeatureTeacher,
  newFeatureTitleTeacher,
  rulesTeacher,
  perfectTeacher,
  newFeatureTitlePlatformTeacher,
  newFeaturePlatformTeacher,
} from "../../helpers/generalTeacherTexts/generalTeacherTexts";
import { OPEN_EVENT as OPEN_PLATFORM_TOUR } from "../PlatformLessonsTour/PlatformLessonsTour";
import PerfectTeacherDropDown from "../PerfectTeacherDropDown/PerfectTeacherDropDown";
import AdminBlock from "./AdminBlock/AdminBlock";
import PlatformNews from "./PlatformNews/PlatformNews";
import { useLocation, useNavigate } from "react-router-dom";
import { format, addHours, isBefore, isAfter, subDays, startOfWeek } from "date-fns";
import { getWeekStartDate } from "../../helpers/weekUtils";

const DAY_NAMES_UA = {
  monday: "Понеділок",
  tuesday: "Вівторок",
  wednesday: "Середа",
  thursday: "Четвер",
  friday: "П'ятниця",
  saturday: "Субота",
  sunday: "Неділя",
};

function GeneralPage({ isStudent, isTeacher, isTrial, isAdmin }) {
  const { data } = useSelector((state) => state.auth);
  const [videoOpen, setVideoOpen] = useState(false);
  const [adviceOpen, setAdviceOpen] = useState(false);
  const [studentRulesOpen, setStudentRulesOpen] = useState(false);
  const [perfectTeacherOpen, setPerfectTeacherOpen] = useState(false);
  const [trialVideo, setTrialVideo] = useState(false);
  const [reschedules, setReschedules] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(getWeekStartDate());
  const navigate = useNavigate();
 
  // Які уроки вже відмічені — з сервера (видно з будь-якого пристрою), + дозвіл адміна на пізні відмітки
  const [serverMarks, setServerMarks] = useState({ keys: [], lateMarkUntil: null });
  useEffect(() => {
    if (!isTeacher || !data?._id) return;
    axios.get(`teachers/${data._id}/marked-slots`)
      .then((res) => setServerMarks({ keys: res.data.slotKeys || [], lateMarkUntil: res.data.lateMarkUntil }))
      .catch(() => {});
  }, [isTeacher, data?._id]);
  const lateUnlocked = Boolean(serverMarks.lateMarkUntil && new Date(serverMarks.lateMarkUntil) > new Date());

  const markedLessons = useMemo(() => {
    const dateKey = format(new Date(), "yyyy-MM-dd");
    const storage = JSON.parse(localStorage.getItem("markedLessons")) || {};
    return storage[dateKey] || [];
  }, []);

  useEffect(() => {
    const storage = JSON.parse(localStorage.getItem("markedLessons")) || {};
    const todayKey = format(new Date(), "yyyy-MM-dd");
    const cleaned = {};

    if (storage[todayKey]) cleaned[todayKey] = storage[todayKey];
    localStorage.setItem("markedLessons", JSON.stringify(cleaned));
  }, []);
  const todayLessons = useMemo(() => {
  if (!isTeacher || !data?.students) return [];

  const now = new Date();
  // Звичайно — сьогодні й учора (вікно 24 год); якщо адмін дозволив пізні відмітки — ще й невідмічені за 6 днів
  const daysBack = lateUnlocked ? 6 : 1;
  const days = Array.from({ length: daysBack + 1 }, (_, back) => {
    const date = subDays(now, back);
    return { back, date, weekday: format(date, "EEEE").toLowerCase() };
  });
  // Перенос/скасування діє тільки в тому тижні, на який його зробили
  const weekOf = (date) => format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-MM-dd");
  const recordWeek = (r) => String(r.weekStartDate || r.lessonReschedule?.date || r.createdAt || "").slice(0, 10);
  const atTime = (date, time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes);
  };
  const visible = (lessonDateTime) =>
    (isAfter(now, addHours(lessonDateTime, -24)) && isBefore(now, addHours(lessonDateTime, 24))) ||
    (lateUnlocked && isBefore(lessonDateTime, now));

  const lessons = [];

  // 🟠 1️⃣ Собираем все отменённые уроки для быстрого поиска
  const canceledLessons = reschedules
    .filter((r) => r.type === "cancel")
    .map((r) => ({
      day: r.lessonReschedule.day,
      time: r.lessonReschedule.time,
      studentId: r.studentId,
      week: recordWeek(r),
    }));

  // 🟣 2️⃣ Основное расписание
  data.students.forEach((student) => {
    student.classSchedule?.forEach((slot) => {
      if (!slot?.day || !slot?.time) return;
      days.filter((d) => d.weekday === slot.day).forEach(({ date }) => {
        const lessonDateTime = atTime(date, slot.time);
        if (!visible(lessonDateTime)) return;
        const isCanceled = canceledLessons.some(
          (c) => c.studentId === student._id && c.day === slot.day && c.time === slot.time && c.week === weekOf(date)
        );
        if (isCanceled) return; // ❌ скасований у цьому тижні — не показуємо
        lessons.push({
          studentId: student._id,
          studentName: student.name,
          time: slot.time,
          day: slot.day,
          lessonDate: lessonDateTime,
          type: "regular",
        });
      });
    });
  });

  // 🟡 3️⃣ Перенесённые уроки (не отменённые)
  reschedules.forEach((r) => {
    if (r.type === "cancel") return;
    const { day, time } = r.lessonReschedule;
    if (!day || !time) return;
    days.filter((d) => d.weekday === day && weekOf(d.date) === recordWeek(r)).forEach(({ date }) => {
      const lessonDateTime = atTime(date, time);
      if (!visible(lessonDateTime)) return;
      lessons.push({
        studentId: r.studentId,
        studentName: r.studentName,
        time,
        day,
        lessonDate: lessonDateTime,
        type: "reschedule",
      });
    });
  });

  return lessons.sort((a, b) => a.lessonDate - b.lessonDate);
}, [data, isTeacher, reschedules, lateUnlocked]);


  useEffect(() => {
    // Переноси за тиждень шукаються по вчителю — учню цей запит не потрібен
    if (!data || data.userType !== "teacher") return;
    // Поточний і минулий тиждень: у понедельник «вчора» — це ще минулий тиждень (його переноси й скасування)
    const weekStart = new Date(currentWeek);
    weekStart.setUTCHours(0, 0, 0, 0);
    const prevWeek = new Date(weekStart.getTime() - 7 * 24 * 3600 * 1000);
    Promise.all(
      [prevWeek, weekStart].map((w) => axios.get(`lesson-reschedule/${data._id}?weekStartDate=${w.toISOString()}`))
    )
      .then((results) => {
        const all = results.flatMap((res) => res.data.lesson || []);
        setReschedules(all.filter((r, i) => all.findIndex((x) => x._id === r._id) === i));
      })
      .catch((err) => console.error(err));
  }, [data, currentWeek]);

  return (
    <PagesWrapper>
      <TopPageTitle value={"Головна"} />
      {(isStudent || isTrial) && <WelcomeCard data={data} isTrial={isTrial} />}
      {isStudent && data && <RenewalBanner data={data} />}
      {isStudent && data && <ReferralCard data={data} />}
      {isStudent && data && <TelegramCard data={data} />}
      {isTeacher && data?.accessType === "en" && <PlatformNews teacher={data} />}
      <styled.Wrapper>
        {(isStudent || isTeacher || isTrial) && (
          <styled.RightPartWrapper isTrial={isTrial}>
            {isTeacher && (
              <styled.LessonsWrapper>
                <styled.BlockTitle>
                  Не забудь зробити відмітку
                </styled.BlockTitle>
                {todayLessons.length === 0 ? (
                  <styled.NoLessons>Уроків немає</styled.NoLessons>
                ) : (
                  todayLessons.map((lesson) => {
                    const lessonKey = `${lesson.studentId}_${lesson.day}_${lesson.time}`;
                    const [hh, mm] = lesson.time.split(":");
                    const serverKey = `${lesson.studentId}_${format(lesson.lessonDate, "yyyy-MM-dd")}_${hh.padStart(2, "0")}:${mm}`;
                    const isMarked = serverMarks.keys.includes(serverKey) || (format(lesson.lessonDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd") && markedLessons.includes(lessonKey));

                    return (
                      <styled.LessonItem key={`${serverKey}_${lesson.type}`} isMarked={isMarked}>
                        <styled.LessonHeader>
                          <styled.StudentName>
                            {lesson.studentName}
                          </styled.StudentName>
                          <styled.Time>{DAY_NAMES_UA[lesson.day] || lesson.day}</styled.Time>
                          <styled.Time>{lesson.time}</styled.Time>
                        </styled.LessonHeader>

                        {isMarked ? (
                          <span>✅ Відмітка зроблена</span>
                        ) : (
                          <styled.CallButton
                            onClick={() =>
                              navigate(
                                `/teacher/lesson-report/${lesson.studentId}/${lesson.day}/${lesson.time}`
                              )
                            }
                          >
                            Зробити відмітку
                          </styled.CallButton>
                        )}
                      </styled.LessonItem>
                    );
                  })
                )}
              </styled.LessonsWrapper>
            )}
            <ProgressBlock
              data={data}
              levels={isStudent || isTrial ? studentLevels : teacherLevels}
              isTrial={isTrial}
            />
            {!isTrial && (isTeacher && data?.accessType === "en" ? (
              <FeatureBlock
                title={newFeatureTitlePlatformTeacher}
                list={newFeaturePlatformTeacher}
                action={{ label: "Як це працює", onClick: () => window.dispatchEvent(new Event(OPEN_PLATFORM_TOUR)) }}
              />
            ) : (
              <FeatureBlock
                title={
                  isStudent ? newFeatureTitleStudent : newFeatureTitleTeacher
                }
                list={isStudent ? newFeatureStudent : newFeatureTeacher}
              />
            ))}
          </styled.RightPartWrapper>
        )}
        {isAdmin && <AdminBlock />}

        {!isAdmin && (
          <styled.DropDownList>
            {!isTrial && (
              <styled.DropDownItem>
                <GeneralDropDown
                  setOpen={setVideoOpen}
                  open={videoOpen}
                  title={"Як користуватися платформою?"}
                  icon={LuPlayCircle}
                />
                {videoOpen && (
                  <styled.DropDownElWrapper>
                    <styled.Video
                      src={
                        isStudent
                          ? "https://www.youtube.com/embed/lx_yH6rVh4w?si=4O_f7le5qcQ_l7wI"
                          : isTeacher
                          ? "https://www.youtube.com/embed/2bRWOp4WVwY?si=P-JuWbqSPNX3IqsW"
                          : ""
                      }
                      title="Як користуватися платформою?"
                      frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerpolicy="strict-origin-when-cross-origin"
                      allowfullscreen
                    ></styled.Video>
                  </styled.DropDownElWrapper>
                )}
              </styled.DropDownItem>
            )}
            {(isStudent || isTrial) && (
              <>
                <styled.DropDownItem>
                  <GeneralDropDown
                    setOpen={setStudentRulesOpen}
                    open={studentRulesOpen}
                    title={"Правила школи"}
                    icon={LuScrollText}
                  />
                  {studentRulesOpen && <AdviceDropDown texts={rules} />}
                </styled.DropDownItem>
                <styled.DropDownItem
                  onClick={() =>
                    navigate(
                      !isTrial
                        ? "/student/student-offerta"
                        : "/trial-student/student-offerta"
                    )
                  }
                >
                  <GeneralDropDown
                    title={"Договір публічної оферти"}
                    withoutArrow
                    icon={LuFileText}
                  />
                </styled.DropDownItem>

                <styled.DropDownItem>
                  <GeneralDropDown
                    setOpen={setAdviceOpen}
                    open={adviceOpen}
                    title={"Корисні поради для вивчення мови!"}
                  />
                  {adviceOpen && <AdviceDropDown texts={adviceStudent} />}
                </styled.DropDownItem>
              </>
            )}
            {isTeacher && (
              <>
                <styled.DropDownItem>
                  <GeneralDropDown
                    setOpen={setAdviceOpen}
                    open={adviceOpen}
                    title={"Корисні поради для викладання!"}
                  />
                  {adviceOpen && <AdviceDropDown texts={adviceTeacher} />}
                </styled.DropDownItem>
                <styled.DropDownItem>
                  <GeneralDropDown
                    setOpen={setStudentRulesOpen}
                    open={studentRulesOpen}
                    title={"Пригадати правила школи"}
                    icon={LuScrollText}
                  />
                  {studentRulesOpen && <AdviceDropDown texts={rulesTeacher} />}
                </styled.DropDownItem>

                <styled.DropDownItem
                  onClick={() => navigate("/teacher/teacher-offerta")}
                >
                  <GeneralDropDown
                    title={"Договір публічної оферти"}
                    withoutArrow
                    icon={LuFileText}
                  />
                </styled.DropDownItem>
                <styled.DropDownItem>
                  <GeneralDropDown
                    setOpen={setPerfectTeacherOpen}
                    open={perfectTeacherOpen}
                    title={"Perfect Teacher"}
                    icon={LuAward}
                  />
                  {perfectTeacherOpen && (
                    <PerfectTeacherDropDown links={perfectTeacher} />
                  )}
                </styled.DropDownItem>
                <styled.DropDownItem>
                <GeneralDropDown
                  setOpen={setTrialVideo}
                  open={trialVideo}
                  title={"Як провести WOW пробний?"}
                  icon={LuSparkles}
                />
                {trialVideo && (
                  <styled.DropDownElWrapper>
                    <styled.Video
                      src={
                        "https://www.youtube.com/embed/7ubaa6JK4mE?si=GTUjE9oIVY4aIZuN"
                      }
                      title="Як провести пробний урок, щоб він був WOW?"
                      frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerpolicy="strict-origin-when-cross-origin"
                      allowfullscreen
                    ></styled.Video>
                  </styled.DropDownElWrapper>
                )}
                </styled.DropDownItem>
              </>
            )}
          </styled.DropDownList>
        )}
      </styled.Wrapper>
    </PagesWrapper>
  );
}
export default GeneralPage;